import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from "recharts";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(3) },
  paper: { padding: theme.spacing(2), height: "100%" },
  statBox: {
    padding: theme.spacing(2),
    textAlign: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    borderRadius: 8
  },
  filterRow: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20, alignItems: "center" },
  sectionTitle: { marginBottom: 12, fontWeight: 600 },
  lowRow: { cursor: "pointer", "&:hover": { background: theme.palette.action.hover } }
}));

const ratingColor = n => {
  if (n <= 2) return "#f44336";
  if (n === 3) return "#ff9800";
  return "#4caf50";
};

const Stars = ({ value }) => (
  <span style={{ color: ratingColor(value) }}>
    {[1,2,3,4,5].map(i =>
      i <= value
        ? <StarIcon key={i} style={{ fontSize: 16 }} />
        : <StarBorderIcon key={i} style={{ fontSize: 16 }} />
    )}
  </span>
);

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = n => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const Satisfaction = () => {
  const classes = useStyles();
  const history = useHistory();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [filters, setFilters] = useState({
    startDate: daysAgo(29),
    endDate: today(),
    userId: "",
    queueId: ""
  });

  useEffect(() => {
    Promise.all([
      api.get("/users").catch(() => ({ data: { users: [] } })),
      api.get("/queue").catch(() => ({ data: [] }))
    ]).then(([u, q]) => {
      setUsers(u.data?.users || []);
      setQueues(Array.isArray(q.data) ? q.data : []);
    });
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/satisfaction/stats", { params });
      setStats(data);
    } catch (err) {
      toastError(err);
    }
  }, [filters]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Typography variant="h5" gutterBottom style={{ fontWeight: 700 }}>
        Satisfação dos Clientes
      </Typography>

      {/* Filtros */}
      <div className={classes.filterRow}>
        <TextField
          label="Data início"
          type="date"
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          value={filters.startDate}
          onChange={e => setFilter("startDate", e.target.value)}
        />
        <TextField
          label="Data fim"
          type="date"
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          value={filters.endDate}
          onChange={e => setFilter("endDate", e.target.value)}
        />
        <TextField
          select
          label="Atendente"
          size="small"
          variant="outlined"
          style={{ minWidth: 160 }}
          value={filters.userId}
          onChange={e => setFilter("userId", e.target.value)}
        >
          <MenuItem value="">Todos</MenuItem>
          {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
        </TextField>
        <TextField
          select
          label="Fila"
          size="small"
          variant="outlined"
          style={{ minWidth: 160 }}
          value={filters.queueId}
          onChange={e => setFilter("queueId", e.target.value)}
        >
          <MenuItem value="">Todas</MenuItem>
          {queues.map(q => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
        </TextField>
      </div>

      {stats && (
        <Grid container spacing={3}>
          {/* Cards de topo */}
          <Grid item xs={12} sm={4}>
            <div className={classes.statBox}>
              <Typography variant="h3" style={{ fontWeight: 700 }}>
                {stats.average ?? "—"}
              </Typography>
              <Typography variant="body1">Média geral</Typography>
              <Typography variant="body2" style={{ opacity: 0.8 }}>
                {stats.total} avaliações
              </Typography>
            </div>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Paper className={classes.paper}>
              <Typography className={classes.sectionTitle}>Distribuição de notas</Typography>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={stats.distribution} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="rating" tickFormatter={v => `${v}★`} />
                  <YAxis allowDecimals={false} />
                  <Tooltip formatter={(v, n) => [v, "Avaliações"]} labelFormatter={v => `Nota ${v}`} />
                  <Bar dataKey="count" name="Avaliações" radius={[4,4,0,0]}
                    fill="#667eea"
                    label={{ position: "top", fontSize: 12 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Evolução diária */}
          {stats.daily.length > 1 && (
            <Grid item xs={12}>
              <Paper className={classes.paper}>
                <Typography className={classes.sectionTitle}>Evolução da média diária</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={stats.daily} margin={{ top: 0, right: 20, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[1, 5]} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="average" name="Média" stroke="#667eea" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {/* Ranking por atendente */}
          {stats.byUser.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Typography className={classes.sectionTitle}>Por atendente</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Atendente</TableCell>
                      <TableCell align="center">Avaliações</TableCell>
                      <TableCell align="center">Média</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.byUser.map(u => (
                      <TableRow key={u.id}>
                        <TableCell>{u.name}</TableCell>
                        <TableCell align="center">{u.count}</TableCell>
                        <TableCell align="center">
                          <span style={{ color: ratingColor(Math.round(u.average)), fontWeight: 600 }}>
                            {u.average}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}

          {/* Por fila */}
          {stats.byQueue.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Typography className={classes.sectionTitle}>Por fila</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fila</TableCell>
                      <TableCell align="center">Avaliações</TableCell>
                      <TableCell align="center">Média</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.byQueue.map(q => (
                      <TableRow key={q.id}>
                        <TableCell>
                          <Chip size="small" label={q.name} style={{ background: q.color, color: "#fff" }} />
                        </TableCell>
                        <TableCell align="center">{q.count}</TableCell>
                        <TableCell align="center">
                          <span style={{ color: ratingColor(Math.round(q.average)), fontWeight: 600 }}>
                            {q.average}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}

          {/* Notas baixas recentes */}
          {stats.lowRatings.length > 0 && (
            <Grid item xs={12}>
              <Paper className={classes.paper}>
                <Typography className={classes.sectionTitle} style={{ color: "#f44336" }}>
                  Notas baixas recentes
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Nota</TableCell>
                      <TableCell>Contato</TableCell>
                      <TableCell>Atendente</TableCell>
                      <TableCell>Fila</TableCell>
                      <TableCell>Fechado em</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.lowRatings.map(r => (
                      <TableRow key={r.ticketId} className={classes.lowRow}
                        onClick={() => history.push(`/tickets/${r.ticketId}`)}>
                        <TableCell>{r.ticketId}</TableCell>
                        <TableCell><Stars value={r.rating} /></TableCell>
                        <TableCell>{r.contact?.name || "—"}</TableCell>
                        <TableCell>{r.user?.name || "—"}</TableCell>
                        <TableCell>{r.queue?.name || "—"}</TableCell>
                        <TableCell>
                          {r.closedAt ? new Date(r.closedAt).toLocaleString("pt-BR") : "—"}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}

          {stats.total === 0 && (
            <Grid item xs={12}>
              <Paper className={classes.paper} style={{ textAlign: "center", padding: 40 }}>
                <Typography color="textSecondary">
                  Nenhuma avaliação no período selecionado.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default Satisfaction;
